import { Button, Card, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { CONTRACT_STATE, HOW_TO_SIGN } from "@/meta/purchase/contract";
import { PAYMENT_TYPE } from "@/meta/purchase/payment";
import { dateFormat, dateTimeFormat, getRealyState } from "@/utils";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { useDetailBack } from "@/utils/hooks/useDetailBack";
import { useUserStore } from "@/views/stores";

export type ContractInfoData = Record<string, any>;

export interface ContractInfoPageProps {
    deleteContract?: (id: number | string) => Promise<unknown>;
    loadDetail?: (id: number | string) => Promise<ContractInfoData>;
    permissions?: string[];
    revokeContract?: (id: number | string) => Promise<unknown>;
}

/** 复用 Vue 合同详情接口。 */
export async function loadContractDetail(id: number | string): Promise<ContractInfoData> {
    const { data } = await request.service.get<ContractInfoData>(`/purchase/contract/find/${id}`);
    return data;
}

/** 复用 Vue 草稿删除和线下合同关闭接口。 */
export async function deletePurchaseContract(id: number | string) {
    const { data } = await request.service.delete(`/purchase/contract/${id}`);
    return data;
}

/** 复用 Vue 线上合同撤回接口。 */
export async function revokePurchaseContract(id: number | string) {
    const { data } = await request.service.put(`/purchase/contract/revoke/${id}`);
    return data;
}

const display = (value: unknown) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);
const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value: unknown) =>
    meta.getIntroByCode(value == null ? undefined : Number(value)) || DEFAULT_VALUE;

/** 兼容后端高位状态编码和测试/历史数据中的低位状态编码。 */
export const getContractRealState = (value: unknown): number | undefined => {
    const state = Number(value);
    if (!Number.isFinite(state)) return undefined;
    return Math.abs(state) >= 1 << 16 ? getRealyState(state) : state;
};

/** 按旧 Vue Scan 分支保留合同状态和签署方式对应的操作可见性。 */
export function isContractDetailActionVisible(
    data: { state?: unknown; signMode?: unknown },
    action: "修改" | "撤回" | "删除" | "关闭"
): boolean {
    const realState = getContractRealState(data.state);
    if (action === "修改" || action === "删除") return CONTRACT_STATE.DRAFT.match(realState);
    if (action === "撤回") {
        return CONTRACT_STATE.SIGNING.match(realState) && HOW_TO_SIGN.ONLINE.match(data.signMode);
    }
    return HOW_TO_SIGN.UNDERLINE.match(data.signMode);
}

/** 合同详情：保留 Vue 字段、状态操作、权限、项目跳转、返回和错误反馈。 */
export function ContractInfoPage({
    deleteContract = deletePurchaseContract,
    loadDetail = loadContractDetail,
    permissions,
    revokeContract = revokePurchaseContract
}: ContractInfoPageProps) {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const goBack = useDetailBack("/purchasingManage/contract?flushed=true");
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canManage = (permissions ?? storePermissions).includes("ROLE_PROJECT_DEAL");
    const [detail, setDetail] = useState<ContractInfoData>();
    const [error, setError] = useState("");
    useEffect(() => {
        if (!id) return;
        void loadDetail(id)
            .then(value => {
                setDetail(value);
                setError("");
            })
            .catch(() => setError("合同详情加载失败"));
    }, [id, loadDetail]);
    const contract = detail;
    const realState = getContractRealState(contract?.state);
    const runContractAction = async (
        action: () => Promise<unknown>,
        successMessage: string,
        confirmMessage?: string
    ) => {
        if (confirmMessage && !(await confirmDialog(confirmMessage))) return;
        try {
            await action();
            notifyOperationSuccess(successMessage);
            void navigate("/purchasingManage/contract?flushed=true");
        } catch {
            setError("合同操作失败");
        }
    };

    return (
        <section aria-label="contract-info-page">
            <Card>
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: display(contract?.id) }]}
                    rightInfos={[
                        {
                            label: "创建时间",
                            value: contract?.createdTime
                                ? dateFormat(contract.createdTime, DATETIME_PICKER_FORMAT)
                                : DEFAULT_VALUE
                        }
                    ]}
                    stateDesc={enumText(CONTRACT_STATE, realState)}
                    title="合同详情"
                />
                {error ? <Alert message={error} role="alert" type="error" /> : null}
                <ZhGrid cols={4} gap={[20, 20]}>
                    <ZhBaseItem label="合同名称" value={display(contract?.name)} />
                    <ZhBaseItem label="状态" value={enumText(CONTRACT_STATE, realState)} />
                    <ZhBaseItem label="类型" value={enumText(PAYMENT_TYPE, contract?.type)} />
                    <ZhBaseItem label="签署方式" value={enumText(HOW_TO_SIGN, contract?.signMode)} />
                    <ZhBaseItem label="项目" span={2}>
                        {contract?.projectId ? (
                            <Link to={`/purchasingManage/project/info/${contract.projectId}`}>
                                {display(contract.projectName)}
                            </Link>
                        ) : (
                            display(contract?.projectName)
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="买方" value={display(contract?.buyerEnterprise?.name)} />
                    <ZhBaseItem label="卖方" value={display(contract?.sellerEnterprise?.name)} />
                    <ZhBaseItem
                        label="签订日期"
                        value={
                            contract?.completionTime
                                ? dateFormat(contract.completionTime, DATE_PICKER_FORMAT)
                                : DEFAULT_VALUE
                        }
                    />
                    <ZhBaseItem
                        label="合同期限"
                        value={
                            contract?.startDate && contract?.finishDate
                                ? `${dateFormat(contract.startDate, DATE_PICKER_FORMAT)} 至 ${dateFormat(contract.finishDate, DATE_PICKER_FORMAT)}`
                                : DEFAULT_VALUE
                        }
                    />
                    <ZhBaseItem label="合同文件" value={display(contract?.fileId)} />
                    {realState === CONTRACT_STATE.INVALID.code || realState === CONTRACT_STATE.INVALIDING.code ? (
                        <>
                            <ZhBaseItem label="作废文件" value={display(contract?.invalidFileId)} />
                            {realState === CONTRACT_STATE.INVALID.code ? (
                                <ZhBaseItem label="作废时间" value={dateTimeFormat(contract?.invalidTime)} />
                            ) : null}
                        </>
                    ) : null}
                </ZhGrid>
                <Space className="mt-6">
                    {canManage && contract && isContractDetailActionVisible(contract, "修改") ? (
                        <Button
                            disabled={banButton}
                            onClick={() => void navigate(`/purchasingManage/contract/edit/${id}`)}
                        >
                            修改
                        </Button>
                    ) : null}
                    {canManage && contract && isContractDetailActionVisible(contract, "撤回") ? (
                        <Button
                            disabled={banButton}
                            onClick={() =>
                                void runContractAction(() => revokeContract(contract.id), "撤回成功", "是否撤回该合同？")
                            }
                        >
                            撤回
                        </Button>
                    ) : null}
                    {canManage && contract && isContractDetailActionVisible(contract, "删除") ? (
                        <Button
                            disabled={banButton}
                            onClick={() =>
                                void runContractAction(() => deleteContract(contract.id), "已删除", "是否删除该合同？")
                            }
                        >
                            删除
                        </Button>
                    ) : null}
                    {canManage && contract && isContractDetailActionVisible(contract, "关闭") ? (
                        <Button
                            disabled={banButton}
                            onClick={() =>
                                void runContractAction(() => deleteContract(contract.id), "已关闭", "是否关闭该合同？")
                            }
                        >
                            关闭
                        </Button>
                    ) : null}
                    <Button onClick={goBack}>返回</Button>
                </Space>
            </Card>
        </section>
    );
}

export default ContractInfoPage;
